"use client";

import Link from "next/link";

/* ────────────────────────────────────────────────────────────────────────────
 * Blog CTA — tracked call-to-action with UTM parameters.
 *
 * All blog CTAs funnel through this component so we can:
 *  1. Append consistent UTM tags
 *  2. Fire a PostHog / analytics event on click
 *  3. Keep styling consistent
 * ──────────────────────────────────────────────────────────────────────── */

interface BlogCtaProps {
  /** The blog post slug — used in utm_content */
  slug: string;
  /** Where the CTA appears: "bottom", "inline", "sidebar" */
  placement: "bottom" | "inline" | "sidebar";
}

function buildUtmUrl(slug: string, placement: string) {
  const params = new URLSearchParams({
    utm_source: "blog",
    utm_medium: "cta",
    utm_campaign: "blog_conversion",
    utm_content: `${slug}_${placement}`,
  });
  return `/try?${params.toString()}`;
}

function trackClick(slug: string, placement: string) {
  // PostHog (loaded globally when available)
  if (typeof window !== "undefined" && (window as any).posthog) {
    (window as any).posthog.capture("blog_cta_clicked", {
      slug,
      placement,
      url: window.location.pathname,
    });
  }
}

export function BlogCtaBottom({ slug, placement = "bottom" }: BlogCtaProps) {
  return (
    <div className="mt-14 rounded-2xl border border-brand/20 bg-gradient-to-br from-brand/5 to-brand/10 p-8 text-center">
      <h2 className="text-2xl font-bold text-foreground">
        Ready to check your document?
      </h2>
      <p className="mx-auto mt-3 max-w-md text-muted-foreground">
        Upload your contract and get an AI-powered risk analysis in minutes.
        First 2 scans free.
      </p>
      <Link
        href={buildUtmUrl(slug, placement)}
        onClick={() => trackClick(slug, placement)}
        className="mt-5 inline-flex items-center gap-2 rounded-xl bg-brand px-7 py-3 text-sm font-semibold text-foreground shadow-lg shadow-brand/20 transition hover:bg-brand/90 hover:shadow-brand/30"
      >
        Scan Now — Free
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M5 12h14M12 5l7 7-7 7" />
        </svg>
      </Link>
    </div>
  );
}

export function BlogCtaInline({ slug, placement = "inline" }: BlogCtaProps) {
  return (
    <div className="my-8 flex items-center gap-4 rounded-xl border border-brand/20 bg-brand/5 p-4">
      <div className="flex-1">
        <p className="text-sm font-medium text-foreground">
          Have a contract you need checked?
        </p>
        <p className="text-xs text-muted-foreground">
          Our AI scans every clause and flags risks in minutes.
        </p>
      </div>
      <Link
        href={buildUtmUrl(slug, placement)}
        onClick={() => trackClick(slug, placement)}
        className="shrink-0 rounded-lg bg-brand px-4 py-2 text-xs font-semibold text-foreground transition hover:bg-brand/90"
      >
        Try Free
      </Link>
    </div>
  );
}

/**
 * Sidebar CTA — sticky 3:4 ratio card for the right column of blog posts.
 */
export function BlogCtaSidebar({ slug, placement = "sidebar" }: BlogCtaProps) {
  return (
    <div className="sticky top-24">
      {/* 3:4 ratio container */}
      <div className="relative overflow-hidden rounded-2xl border border-brand/20 bg-gradient-to-br from-brand/5 via-brand/10 to-brand/5" style={{ aspectRatio: "3/4" }}>
        <div className="flex h-full flex-col items-center justify-center p-6 text-center">
          {/* Icon */}
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-brand/15">
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-brand"
            >
              <path d="M9 12l2 2 4-4" />
              <path d="M12 3c1.2 0 2.4.6 3 1.5A3.5 3.5 0 0 1 19 8v1a2 2 0 0 1 0 4v1a3.5 3.5 0 0 1-4 3.5c-.6.9-1.8 1.5-3 1.5s-2.4-.6-3-1.5A3.5 3.5 0 0 1 5 14v-1a2 2 0 0 1 0-4V8a3.5 3.5 0 0 1 4-3.5C9.6 3.6 10.8 3 12 3" />
            </svg>
          </div>

          <h3 className="text-lg font-bold text-foreground leading-tight">
            Got a contract?
          </h3>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
            Upload your document and get an AI-powered clause-by-clause risk analysis in minutes.
          </p>

          {/* Feature list */}
          <ul className="mt-4 space-y-2 text-left text-xs text-muted-foreground">
            <li className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-brand flex-shrink-0" />
              Indian law verified
            </li>
            <li className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-brand flex-shrink-0" />
              Red / amber / green flags
            </li>
            <li className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-brand flex-shrink-0" />
              First 2 scans free
            </li>
          </ul>

          <Link
            href={buildUtmUrl(slug, placement)}
            onClick={() => trackClick(slug, placement)}
            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brand px-5 py-3 text-sm font-semibold text-foreground shadow-lg shadow-brand/20 transition hover:bg-brand/90 hover:shadow-brand/30"
          >
            Scan Now — Free
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );
}
